import {
  Badge,
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Skeleton,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VisuallyHidden
} from '@chakra-ui/react';
import {
  faBars,
  faPenToSquare,
  faTrash
} from '@fortawesome/pro-regular-svg-icons';
import { faFile, faUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonText, MenuItem } from '@morressier/alembic';
import { ITags } from '@publishing-engine/domain/models';
import { DeleteUsage } from './ManageTags';

type TagTableColumn = {
  key: string;
  label: string;
  width?: string;
  textAlign?: 'left' | 'center';
};

/**
 * Columns rendered for a given tag usage. Shared by the header row and the
 * loading skeleton so the two can never drift out of sync.
 *
 * Widths are percentages because the table uses a fixed layout — the table
 * always fits its container rather than overflowing into a scrollbar, and the
 * cells truncate (each one has a tooltip holding the full value).
 */
export const getTagTableColumns = (usage: string): TagTableColumn[] => [
  { key: 'name', label: 'Tag Name', width: '18%' },
  ...(usage === 'submissions'
    ? [
        {
          key: 'journal',
          label: 'Journal',
          width: '12%',
          textAlign: 'center' as const
        }
      ]
    : []),
  ...(usage === 'users'
    ? [
        {
          key: 'scope',
          label: 'Scope',
          width: '12%',
          textAlign: 'center' as const
        }
      ]
    : []),
  { key: 'description', label: 'Description', width: '20%' },
  { key: 'category', label: 'Category', width: '14%' },
  { key: 'categoryDescription', label: 'Category Description', width: '20%' },
  {
    key: 'categoryColor',
    label: 'Category Color',
    width: '10%',
    textAlign: 'center'
  },
  { key: 'actions', label: '', width: '6%' }
];

export const TagTableHeaderRow = ({ usage }: { usage: string }) => (
  <Thead>
    <Tr
      data-testId="tags-table-header"
      sx={{
        '& th': {
          textTransform: 'none',
          color: 'black'
        }
      }}
      background="white"
    >
      {getTagTableColumns(usage).map(({ key, label, width, textAlign }) => (
        <Th key={key} p={4} width={width} textAlign={textAlign}>
          {label ? (
            <Text textStyle="headingItem">{label}</Text>
          ) : (
            <VisuallyHidden>Actions</VisuallyHidden>
          )}
        </Th>
      ))}
    </Tr>
  </Thead>
);

/** Placeholder rows shown while a page of tags is being fetched. */
export const TagTableSkeletonRows = ({
  columnCount
}: {
  columnCount: number;
}) => (
  <>
    {[1, 2].map(row => (
      <Tr key={row} background="white">
        {Array.from({ length: columnCount }, (_, column) => (
          <Td key={column}>
            <Skeleton height={6} />
          </Td>
        ))}
      </Tr>
    ))}
  </>
);

const TagTableRow = ({
  tagData,
  openEditTagModal,
  opeDeleteTagModal
}: {
  tagData: ITags;
  openEditTagModal: (
    id: string,
    name: string,
    description: string,
    usage: string,
    submissionContextId: string,
    tagCategoryId: string,
    tagCategory: string,
    tagColor: string,
    tagDescription: string
  ) => void;
  opeDeleteTagModal: (id: string, usage: DeleteUsage) => void;
}) => {
  const {
    id,
    name,
    description,
    tagCategory,
    tagColor,
    tagDescription,
    usage,
    journal,
    submissionContextId,
    tagCategoryId
  } = tagData;

  return (
    <Tr
      key={name}
      _last={{ borderBottom: 'none' }}
      data-testId="tags-table-row"
      background="white"
    >
      <Td width="200px" maxW="200px">
        <Tooltip label={name}>
          <Box display="flex">
            <Badge
              size="context"
              variant="neutral"
              maxW="100%"
              minW={0}
              overflow="hidden"
            >
              <Flex gap={1} alignItems={'center'}>
                <FontAwesomeIcon icon={usage === 'users' ? faUser : faFile} />
                <Text noOfLines={1} minW={0}>
                  {name}
                </Text>
              </Flex>
            </Badge>
          </Box>
        </Tooltip>
      </Td>

      {usage === 'submissions' ? (
        <Td textAlign="center">
          <Tooltip
            hasArrow
            borderRadius={4}
            placement="top"
            label={journal?.name || 'N/A'}
          >
            <Text
              textStyle="textContext"
              noOfLines={1}
              whiteSpace={'break-spaces'}
            >
              {journal?.shortname ? journal.shortname.toUpperCase() : 'N/A'}
            </Text>
          </Tooltip>
        </Td>
      ) : null}

      {usage === 'users' ? (
        <Td textAlign="center">
          <Tooltip
            hasArrow
            borderRadius={4}
            placement="top"
            label={journal?.name || 'Global'}
          >
            <Text
              textStyle="textContext"
              noOfLines={1}
              whiteSpace={'break-spaces'}
            >
              {journal?.shortname ? journal.shortname.toUpperCase() : 'Global'}
            </Text>
          </Tooltip>
        </Td>
      ) : null}

      <Td textAlign="left">
        <Tooltip hasArrow borderRadius={4} placement="top" label={description}>
          <Text noOfLines={1} whiteSpace={'break-spaces'}>
            {description}
          </Text>
        </Tooltip>
      </Td>

      <Td textAlign="left">
        <Tooltip hasArrow borderRadius={4} placement="top" label={tagCategory}>
          <Text noOfLines={1}>{tagCategory || 'N/A'}</Text>
        </Tooltip>
      </Td>

      <Td textAlign="left">
        <Tooltip
          hasArrow
          borderRadius={4}
          placement="top"
          label={tagDescription}
        >
          <Text noOfLines={1} whiteSpace={'break-spaces'}>
            {tagDescription || 'N/A'}
          </Text>
        </Tooltip>
      </Td>

      <Td textAlign="left">
        <Tooltip hasArrow borderRadius={4} placement="top" label={tagColor}>
          <Flex alignItems="center" justifyContent="center" gap={2}>
            <Box
              w="30px"
              h="30px"
              bg={tagColor}
              border="1px solid"
              borderColor={'gray.300'}
              borderRadius="4px"
              shadow={'sm'}
            />
          </Flex>
        </Tooltip>
      </Td>

      <Td>
        <Flex justifyContent="flex-end" width="100%">
          <Menu aria-label="tag actions menu">
            <MenuButton>
              <ButtonText>
                <Flex gap={2} alignItems="center">
                  <FontAwesomeIcon size="lg" icon={faBars} />
                </Flex>
              </ButtonText>
            </MenuButton>
            <MenuList>
              <MenuItem
                ariaLabel="Edit Tag"
                onClick={() =>
                  openEditTagModal(
                    id,
                    name,
                    description,
                    usage,
                    submissionContextId || '',
                    tagCategoryId || '',
                    tagCategory,
                    tagColor,
                    tagDescription
                  )
                }
              >
                <Flex gap={2} alignItems="center">
                  <FontAwesomeIcon icon={faPenToSquare} />
                  <Text textStyle="context">Edit</Text>
                </Flex>
              </MenuItem>

              <MenuItem
                ariaLabel="Delete Tag"
                onClick={() => opeDeleteTagModal(id, usage as DeleteUsage)}
              >
                <Flex
                  gap={2}
                  color="actionSecondaryCautionContent"
                  alignItems="center"
                >
                  <FontAwesomeIcon icon={faTrash} />
                  <Text textStyle="context">Delete</Text>
                </Flex>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Td>
    </Tr>
  );
};

export default TagTableRow;
